(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-nut-pdp-routine]';

  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const money = (cents, root) => {
    const amount = Math.max(0, Math.round(num(cents, 0)));
    const format = root.dataset.moneyFormat || '';
    const currency = root.dataset.currency || 'EGP';

    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function' &&
      format
    ) {
      try {
        return window.Shopify.formatMoney(amount, format);
      } catch (_error) {}
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency
      }).format(amount / 100);
    } catch (_error) {
      return `${(amount / 100).toFixed(2)} ${currency}`;
    }
  };

  const replace = (template, values = {}) => {
    let output = String(template || '');

    Object.entries(values).forEach(([key, value]) => {
      output = output.split(`[${key}]`).join(String(value));
    });

    return output;
  };

  const mainProductForm = () => {
    const forms = Array.from(
      document.querySelectorAll('form[action*="/cart/add"]')
    );

    return (
      forms.find((form) => form.querySelector('[name="id"]')) ||
      forms[0] ||
      null
    );
  };

  const currentVariant = (root) => {
    const script = root.querySelector(
      '[data-nut-pdp-routine-current-variants]'
    );

    const currentSlot = root.querySelector(
      '[data-nut-pdp-routine-current]'
    );

    const form = mainProductForm();
    const input = form?.querySelector('[name="id"]');

    const fallbackId = num(
      currentSlot?.dataset.variantId,
      0
    );

    const id = num(
      input?.value,
      fallbackId
    );

    if (!script) return null;

    try {
      const variants = JSON.parse(script.textContent || '[]');

      return (
        variants.find((variant) => num(variant.id) === id) ||
        variants[0] ||
        null
      );
    } catch (_error) {
      return null;
    }
  };

  const packState = () => {
    const selected = document.querySelector(
      '.nut-pdp-section [data-nut-pdp-pack-option][aria-checked="true"]'
    );

    if (!selected) {
      return {
        quantity: 1,
        discount: 0,
        label: ''
      };
    }

    return {
      quantity: Math.max(
        1,
        num(
          selected.dataset.quantity ??
          selected.dataset.nutPdpPackQuantity,
          1
        )
      ),

      discount: Math.max(
        0,
        num(
          selected.dataset.discount ??
          selected.dataset.nutPdpPackDiscount,
          0
        )
      ),

      label:
        selected
          .querySelector(
            '.nut-pdp-pack__copy strong, .nut-pdp-pack__title'
          )
          ?.textContent?.trim() || ''
    };
  };

  const variantData = (item) => {
    const control = item.querySelector(
      '[data-nut-pdp-routine-variant]'
    );

    if (!control) return null;

    return {
      id: num(control.value, 0),
      price: num(control.dataset.price, 0),
      available: control.dataset.available !== 'false',
      image: control.dataset.image || ''
    };
  };

  const selectedAddons = (root) =>
    Array.from(
      root.querySelectorAll('[data-nut-pdp-routine-item]')
    )
      .filter(
        (item) => item.dataset.selected === 'true'
      )
      .map((item) => ({
        item,
        variant: variantData(item)
      }))
      .filter(
        ({ variant }) =>
          variant &&
          variant.id &&
          variant.available
      );

  const routineRate = (root, requestedSteps) => {
    const maxSteps = Math.max(
      1,
      num(root.dataset.maxSteps, 1)
    );

    const steps = Math.min(
      requestedSteps,
      maxSteps
    );

    if (steps >= 4 && maxSteps >= 4) {
      return num(
        root.dataset.discountFour,
        0
      ) / 100;
    }

    if (steps >= 3 && maxSteps >= 3) {
      return num(
        root.dataset.discountThree,
        0
      ) / 100;
    }

    if (steps >= 2 && maxSteps >= 2) {
      return num(
        root.dataset.discountTwo,
        0
      ) / 100;
    }

    return 0;
  };

  const calculate = (root) => {
    const variant = currentVariant(root);
    const pack = packState();

    /*
     * STEP 1 — Current/base product:
     * Respect the selected pack quantity + pack discount first.
     */
    const currentGross =
      num(variant?.price, 0) *
      pack.quantity;

    const packSaving = Math.round(
      currentGross *
      (pack.discount / 100)
    );

    const currentAfterPack = Math.max(
      0,
      currentGross - packSaving
    );

    /*
     * STEP 2 — Add-on products:
     * Their normal selected-variant prices.
     */
    const addons = selectedAddons(root);

    const addonsGross = addons.reduce(
      (total, addon) =>
        total + addon.variant.price,
      0
    );

    /*
     * STEP 3 — Routine tier:
     * Count the base product as Step 1.
     * 2 total steps => discount_2_steps
     * 3 total steps => discount_3_steps
     * 4 total steps => discount_4_steps
     */
    const maxSteps = Math.max(
      1,
      num(root.dataset.maxSteps, 1)
    );

    const steps = Math.min(
      1 + addons.length,
      maxSteps
    );

    const rate = routineRate(
      root,
      steps
    );

    /*
     * STEP 4 — IMPORTANT:
     * Routine discount applies to THE WHOLE ROUTINE SUBTOTAL,
     * including the current/base product.
     *
     * If a pack discount exists, it is applied first.
     * Then the routine tier discount is applied to:
     * currentAfterPack + addonsGross
     */
    const routineSubtotal =
      currentAfterPack +
      addonsGross;

    const routineSaving = Math.round(
      routineSubtotal *
      rate
    );

    const total = Math.max(
      0,
      routineSubtotal -
      routineSaving
    );

    return {
      variant,
      pack,
      addons,
      maxSteps,
      steps,
      rate,

      currentGross,
      packSaving,
      currentAfterPack,

      addonsGross,

      routineSubtotal,
      routineSaving,
      total
    };
  };

  const summaryLine = (
    container,
    label,
    value,
    saving = false
  ) => {
    const line = document.createElement('div');

    line.className =
      `nut-pdp-routine__summary-line${saving ? ' is-saving' : ''}`;

    const left = document.createElement('span');
    left.textContent = label;

    const right = document.createElement('strong');
    right.textContent = value;

    line.append(
      left,
      right
    );

    container.appendChild(line);
  };

  const render = (root) => {
    const calc = calculate(root);

    const currentSlot = root.querySelector(
      '[data-nut-pdp-routine-current]'
    );

    const currentPrice = root.querySelector(
      '[data-nut-pdp-routine-current-price]'
    );

    if (currentPrice) {
      currentPrice.textContent =
        money(
          calc.currentGross,
          root
        );
    }

    const currentPack = root.querySelector(
      '[data-nut-pdp-routine-current-pack]'
    );

    if (currentPack) {
      currentPack.textContent =
        calc.pack.label ||
        `${calc.pack.quantity} ×`;
    }

    if (
      currentSlot &&
      calc.variant
    ) {
      currentSlot.dataset.variantId =
        String(calc.variant.id);

      currentSlot.dataset.unitPrice =
        String(calc.variant.price);

      currentSlot.dataset.available =
        String(Boolean(calc.variant.available));
    }

    root
      .querySelectorAll(
        '[data-nut-pdp-routine-item]'
      )
      .forEach((item) => {
        const selected =
          item.dataset.selected === 'true';

        const variant =
          variantData(item);

        const toggle =
          item.querySelector(
            '[data-nut-pdp-routine-toggle]'
          );

        const image =
          item.querySelector(
            '[data-nut-pdp-routine-image]'
          );

        item.classList.toggle(
          'is-selected',
          selected
        );

        if (toggle) {
          toggle.setAttribute(
            'aria-pressed',
            selected
              ? 'true'
              : 'false'
          );

          toggle.disabled =
            !variant?.available;

          toggle.textContent =
            !variant?.available
              ? root.dataset.labelSoldOut
              : selected
                ? root.dataset.labelAdded
                : root.dataset.labelAdd;
        }

        if (
          image &&
          variant?.image
        ) {
          image.src = variant.image;
        }
      });

    root
      .querySelectorAll(
        '[data-nut-pdp-routine-ladder] [data-step]'
      )
      .forEach((step) => {
        const stepNumber =
          num(
            step.dataset.step,
            0
          );

        step.classList.toggle(
          'is-on',
          stepNumber <= calc.steps
        );
      });

    const summarySub =
      root.querySelector(
        '[data-nut-pdp-routine-summary-sub]'
      );

    if (summarySub) {
      if (calc.steps === 1) {
        summarySub.textContent =
          replace(
            root.dataset.labelOneStep,
            {
              percent:
                Math.round(
                  num(
                    root.dataset.discountTwo,
                    0
                  )
                )
            }
          );
      } else {
        summarySub.textContent =
          replace(
            root.dataset.labelSteps,
            {
              count: calc.steps
            }
          );
      }
    }

    const lines =
      root.querySelector(
        '[data-nut-pdp-routine-summary-lines]'
      );

    if (lines) {
      lines.innerHTML = '';

      const currentName =
        currentSlot
          ?.querySelector(
            '.nut-pdp-routine__product-title'
          )
          ?.textContent?.trim() || '';

      summaryLine(
        lines,
        currentName,
        money(
          calc.currentGross,
          root
        )
      );

      if (calc.packSaving > 0) {
        summaryLine(
          lines,
          root.dataset.labelPackSaving,
          `− ${money(calc.packSaving, root)}`,
          true
        );
      }

      calc.addons.forEach(
        ({ item, variant }) => {
          const name =
            item
              .querySelector(
                '.nut-pdp-routine__product-title'
              )
              ?.textContent?.trim() || '';

          summaryLine(
            lines,
            name,
            money(
              variant.price,
              root
            )
          );
        }
      );

      if (calc.routineSaving > 0) {
        summaryLine(
          lines,
          replace(
            root.dataset.labelRoutineDiscount,
            {
              percent:
                Math.round(
                  calc.rate * 100
                )
            }
          ),
          `− ${money(calc.routineSaving, root)}`,
          true
        );
      }
    }

    const total =
      root.querySelector(
        '[data-nut-pdp-routine-total]'
      );

    if (total) {
      total.textContent =
        money(
          calc.total,
          root
        );
    }

    const shipping =
      root.querySelector(
        '[data-nut-pdp-routine-shipping]'
      );

    if (shipping) {
      const threshold =
        num(
          root.dataset.freeShippingThreshold,
          85000
        );

      const remaining =
        Math.max(
          0,
          threshold - calc.total
        );

      if (remaining <= 0) {
        shipping.innerHTML =
          `<strong>${root.dataset.labelFreeDelivery}</strong>`;
      } else {
        shipping.textContent =
          replace(
            root.dataset.labelShippingRemaining,
            {
              amount:
                money(
                  remaining,
                  root
                )
            }
          );
      }
    }

    return calc;
  };

  const selectVariantPill = (
    root,
    pill
  ) => {
    const item =
      pill.closest(
        '[data-nut-pdp-routine-item]'
      );

    if (
      !item ||
      pill.disabled
    ) {
      return;
    }

    const hiddenInput =
      item.querySelector(
        '[data-nut-pdp-routine-variant]'
      );

    const choices =
      item.querySelectorAll(
        '[data-nut-pdp-routine-variant-choice]'
      );

    choices.forEach((choice) => {
      const active =
        choice === pill;

      choice.classList.toggle(
        'is-active',
        active
      );

      choice.setAttribute(
        'aria-pressed',
        active
          ? 'true'
          : 'false'
      );
    });

    if (hiddenInput) {
      hiddenInput.value =
        pill.dataset.variantId || '';

      hiddenInput.dataset.price =
        pill.dataset.price || '0';

      hiddenInput.dataset.available =
        pill.dataset.available || 'false';

      hiddenInput.dataset.image =
        pill.dataset.image || '';

      hiddenInput.dispatchEvent(
        new Event(
          'change',
          {
            bubbles: true
          }
        )
      );
    }

    render(root);
  };

  const buildItems = (
    root,
    calc
  ) => {
    const routineId =
      `nut-routine-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const items = [];

    if (
      calc.variant?.id &&
      calc.variant.available !== false
    ) {
      items.push({
        id: calc.variant.id,
        quantity: calc.pack.quantity,
        properties: {
          _nut_routine_id: routineId,
          _nut_routine_step: '1',
          _nut_routine_base: 'true',
          _nut_pack_discount:
            String(calc.pack.discount || 0),
          _nut_routine_rate:
            String(
              Math.round(
                calc.rate * 100
              )
            ),
          _nut_routine_discount_scope:
            'whole_routine'
        }
      });
    }

    calc.addons.forEach(
      ({ item, variant }) => {
        items.push({
          id: variant.id,
          quantity: 1,
          properties: {
            _nut_routine_id: routineId,
            _nut_routine_step:
              String(item.dataset.step || ''),
            _nut_routine_addon: 'true',
            _nut_routine_rate:
              String(
                Math.round(
                  calc.rate * 100
                )
              ),
            _nut_routine_discount_scope:
              'whole_routine'
          }
        });
      }
    );

    return items;
  };

  const getCartSectionIds = () => {
    return Array.from(
      document.querySelectorAll('[ref="hdt-cart"][section-id]')
    )
      .map((element) => element.getAttribute('section-id'))
      .filter((id, index, ids) => id && ids.indexOf(id) === index);
  };

  const getFreshCart = async () => {
    const rootUrl =
      window.Shopify?.routes?.root ||
      '/';

    const response =
      await fetch(
        `${rootUrl}cart.js`,
        {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      );

    const cart =
      await response.json();

    if (!response.ok) {
      throw new Error(
        cart?.description ||
        cart?.message ||
        'cart_fetch_failed'
      );
    }

    return cart;
  };

  const updateCartCount = (cart) => {
    if (
      !cart ||
      typeof cart.item_count !== 'number'
    ) {
      return;
    }

    const count =
      String(cart.item_count);

    const directSelectors = [
      'hdt-cart-count',
      '[data-cart-count]',
      '[data-header-cart-count]',
      '[data-hdt-cart-count]',
      '.hdt-cart-count',
      '.hdt-header-cart-count',
      '.hdt-site-nav__cart-count',
      '.cart-count',
      '.cart-count-number'
    ];

    directSelectors.forEach((selector) => {
      document
        .querySelectorAll(selector)
        .forEach((element) => {
          if (
            !element.children ||
            element.children.length === 0
          ) {
            element.textContent = count;
          }

          element.setAttribute(
            'data-cart-count',
            count
          );

          if (cart.item_count > 0) {
            element.removeAttribute('hidden');
          }
        });
    });

    document.documentElement.setAttribute(
      'data-cart-count',
      count
    );

    document.body.setAttribute(
      'data-cart-count',
      count
    );

    document.dispatchEvent(
      new CustomEvent(
        'cart:count:update',
        {
          bubbles: true,
          detail: {
            item_count: cart.item_count,
            total_price: cart.total_price || 0,
            cart
          }
        }
      )
    );
  };

  const openCartDrawerFallback = () => {
    const trigger =
      document.querySelector(
        '[aria-controls="CartDrawer"], ' +
        '[data-cart-drawer-toggle], ' +
        '[data-trigger-cart], ' +
        '[data-cart-open], ' +
        '.hdt-header-cart'
      );

    if (trigger) {
      try {
        trigger.click();
        return true;
      } catch (_error) {}
    }

    const drawer =
      document.querySelector(
        'hdt-cart-drawer, #CartDrawer'
      );

    if (
      drawer &&
      typeof drawer.open === 'function'
    ) {
      try {
        drawer.open();
        return true;
      } catch (_error) {}
    }

    return false;
  };

  const syncHdtCart = async (
    addResponse,
    items
  ) => {
    const cart =
      await getFreshCart();

    updateCartCount(cart);

    const hasSections =
      addResponse &&
      addResponse.sections &&
      typeof addResponse.sections === 'object' &&
      Object.keys(addResponse.sections).length > 0;

    if (hasSections) {
      /*
       * IMPORTANT FOR KALLES / HDT:
       * hdt-cart-drawer listens to the exact "cart:update" event.
       * cartData MUST be the add.js response containing sections.
       * resource MUST be the fresh /cart.js cart object.
       * source MUST be "product-form-component" so the drawer opens.
       */
      document.dispatchEvent(
        new CustomEvent(
          'cart:update',
          {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'nut-pdp-routine',
              cartData: addResponse,
              actionAfterATC: 'open_cart_drawer',
              source: 'product-form-component',
              data: {
                source: 'nut-pdp-routine',
                itemCount: cart.item_count,
                productId:
                  String(
                    document
                      .querySelector(
                        '[data-nut-pdp-routine]'
                      )
                      ?.dataset.productId || ''
                  ),
                sections: addResponse.sections
              }
            }
          }
        )
      );

      return cart;
    }

    /*
     * Fallback only.
     * If no bundled sections came back, ask HDT to reload its own cart section.
     */
    document.dispatchEvent(
      new CustomEvent(
        'cart:reload',
        {
          bubbles: true
        }
      )
    );

    window.setTimeout(
      openCartDrawerFallback,
      220
    );

    return cart;
  };

  const addRoutine = async (
    root
  ) => {
    const button =
      root.querySelector(
        '[data-nut-pdp-routine-add]'
      );

    const label =
      root.querySelector(
        '[data-nut-pdp-routine-add-label]'
      );

    const errorBox =
      root.querySelector(
        '[data-nut-pdp-routine-error]'
      );

    if (!button) return;

    const calc =
      render(root);

    const items =
      buildItems(
        root,
        calc
      );

    if (!items.length) return;

    const originalLabel =
      label?.textContent || '';

    button.disabled = true;

    if (label) {
      label.textContent =
        root.dataset.labelAdding;
    }

    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    try {
      const rootUrl =
        window.Shopify?.routes?.root ||
        '/';

      const sectionIds =
        getCartSectionIds();

      const requestBody = {
        items
      };

      /*
       * Shopify bundled section rendering:
       * ask for the exact HDT cart drawer section in the SAME add.js request.
       */
      if (sectionIds.length) {
        requestBody.sections =
          sectionIds.join(',');

        requestBody.sections_url =
          window.location.pathname;
      }

      const response =
        await fetch(
          `${rootUrl}cart/add.js`,
          {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: {
              'Content-Type':
                'application/json',
              Accept:
                'application/json',
              'X-Requested-With':
                'XMLHttpRequest'
            },
            body:
              JSON.stringify(
                requestBody
              )
          }
        );

      const payload =
        await response.json();

      if (!response.ok) {
        throw new Error(
          payload?.description ||
          payload?.message ||
          root.dataset.labelError
        );
      }

      /*
       * Do NOT open the drawer yet.
       * First give Kalles/HDT fresh section HTML + fresh cart state.
       */
      const cart =
        await syncHdtCart(
          payload,
          items
        );

      if (label) {
        label.textContent =
          root.dataset.labelAddedCart;
      }

      /*
       * Verification: the cart must already contain the lines before we finish.
       * This mirrors the working bundle-builder fix used on this theme.
       */
      if (
        !cart ||
        typeof cart.item_count !== 'number'
      ) {
        throw new Error(
          root.dataset.labelError
        );
      }

      window.setTimeout(
        () => {
          if (label) {
            label.textContent =
              originalLabel;
          }

          button.disabled = false;
        },
        1400
      );
    } catch (error) {
      console.error(
        '[NUT PDP Routine] Cart add failed:',
        error
      );

      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent =
          error?.message ||
          root.dataset.labelError;
      }

      if (label) {
        label.textContent =
          originalLabel;
      }

      button.disabled = false;
    }
  };

  const initRoot = (root) => {
    if (
      root.dataset.nutPdpRoutineInitialized === 'true'
    ) {
      return;
    }

    root.dataset.nutPdpRoutineInitialized =
      'true';

    root.addEventListener(
      'click',
      (event) => {
        const variantChoice =
          event.target.closest(
            '[data-nut-pdp-routine-variant-choice]'
          );

        if (
          variantChoice &&
          root.contains(variantChoice)
        ) {
          selectVariantPill(
            root,
            variantChoice
          );

          return;
        }

        const toggle =
          event.target.closest(
            '[data-nut-pdp-routine-toggle]'
          );

        if (
          toggle &&
          root.contains(toggle)
        ) {
          const item =
            toggle.closest(
              '[data-nut-pdp-routine-item]'
            );

          if (
            !item ||
            toggle.disabled
          ) {
            return;
          }

          item.dataset.selected =
            item.dataset.selected === 'true'
              ? 'false'
              : 'true';

          render(root);

          return;
        }

        const add =
          event.target.closest(
            '[data-nut-pdp-routine-add]'
          );

        if (
          add &&
          root.contains(add)
        ) {
          addRoutine(root);
        }
      }
    );

    root.addEventListener(
      'change',
      (event) => {
        if (
          event.target.matches(
            '[data-nut-pdp-routine-variant]'
          )
        ) {
          render(root);
        }
      }
    );

    document.addEventListener(
      'change',
      (event) => {
        if (
          event.target.matches(
            '.nut-pdp-section [name="id"], ' +
            '.nut-pdp-section [data-nut-pdp-pack-option]'
          )
        ) {
          window.setTimeout(
            () => render(root),
            40
          );
        }
      },
      true
    );

    [
      'variant:change',
      'product:variant-change'
    ].forEach((name) => {
      document.addEventListener(
        name,
        () => {
          window.setTimeout(
            () => render(root),
            40
          );
        }
      );
    });

    render(root);
  };

  const init = () =>
    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initRoot);

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    init
  );
})();